/**
 * CatalogPicker.jsx — shared catalog-offering manager used by both
 * VendorDashboard (services + credit) and AgencySettings ("Services I offer").
 *
 * Props:
 *   - category: 'service' | 'credit' | 'agency'
 *   - accent:   hex color for buttons
 *   - listEndpoint: e.g. /vendor/me or /agencies/me — used to GET current
 *                   catalog_offerings; we re-fetch after each save.
 *   - saveEndpoint: e.g. /vendor/me/catalog-offerings or /agency/me/catalog-offerings
 *   - parentKey:  'vendor' | 'agency' — name of the top-level key in GET response
 *
 * Vendors cannot edit the catalog item name (locked by design for filterability).
 * They CAN set price + cadence + (optional) turnaround_days + (internal) margin_pct.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/api';
import { useCurrency } from '@/hooks/useCurrency';
import { toast } from 'sonner';
import { Plus, Trash, X, MagnifyingGlass, CaretDown, FloppyDisk, Lightbulb, FileText, Check } from '@phosphor-icons/react';

const CADENCES = [
  { value: 'one_time',  label: 'One-time' },
  { value: 'monthly',   label: 'Per month' },
  { value: 'quarterly', label: 'Per quarter' },
  { value: 'annual',    label: 'Per year' },
];

const CatalogPicker = ({ category, accent, listEndpoint, saveEndpoint, parentKey }) => {
  const { format } = useCurrency();
  const [catalog, setCatalog] = useState([]);
  const [own, setOwn] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(null);   // catalog item being added/edited
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);   // this vendor's own catalog requests

  const reload = async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        api.get(`/catalog/${category}`),
        api.get(`${listEndpoint}`),
      ]);
      setCatalog(c.data.items || []);
      setOwn((m.data[parentKey]?.catalog_offerings) || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load catalog'); }
    finally { setLoading(false); }
    // Our own "Suggest new" requests — non-fatal if this fails, the grid just omits them.
    api.get(`/catalog/${category}/my-suggestions`)
      .then((r) => setSuggestions(r.data.items || []))
      .catch(() => setSuggestions([]));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [category]);

  const ownByItemId = useMemo(() => Object.fromEntries(own.map((o) => [o.catalog_item_id, o])), [own]);
  const availableItems = useMemo(() => catalog.filter((c) => !ownByItemId[c.id]), [catalog, ownByItemId]);
  // Pending/rejected requests show with a status badge; once approved and not yet
  // priced they prompt for pricing; once priced they're an ordinary offering card.
  const suggestionCards = useMemo(
    () => suggestions.filter((s) => s.status !== 'approved' || !ownByItemId[s.id]),
    [suggestions, ownByItemId],
  );

  const remove = async (offering) => {
    if (!window.confirm(`Remove ${offering.catalog_item_name}?`)) return;
    try {
      await api.delete(`${saveEndpoint}/${offering.catalog_item_id}`);
      toast.success('Removed');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="text-sm text-text-muted py-4">Loading catalog…</div>;

  return (
    <div data-testid={`catalog-picker-${category}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-text-muted">
          Pick from the {catalog.length}-item {category} catalog. Founders search by these exact names &mdash; that&rsquo;s why naming is locked.
        </p>
        <div className="flex gap-2">
          <button onClick={() => setSuggestOpen(true)} data-testid="catalog-suggest-open" className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-border hover:border-text-primary inline-flex items-center gap-1 text-text-secondary">
            <Lightbulb size={10} weight="bold" /> Suggest new
          </button>
          <button onClick={() => setPicker({ mode: 'new' })} data-testid="catalog-add-open" className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg text-white inline-flex items-center gap-1" style={{ backgroundColor: accent }}>
            <Plus size={10} weight="bold" /> Add offering
          </button>
        </div>
      </div>

      {own.length === 0 && suggestionCards.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-6 text-center text-sm text-text-muted bg-background">
          No catalog offerings yet. Tap <strong className="text-text-primary">Add offering</strong> to publish your first product.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {own.map((o) => {
            const isCredit = o.catalog_item_category === 'credit';
            const ct = o.credit_terms || {};
            const fmtRange = (a, b) => (a != null && b != null) ? `${Number(a).toLocaleString('en-IN')}–${Number(b).toLocaleString('en-IN')}` : (a != null ? `from ${Number(a).toLocaleString('en-IN')}` : (b != null ? `up to ${Number(b).toLocaleString('en-IN')}` : ''));
            return (
            <div key={o.catalog_item_id} className="bg-background border border-border rounded-xl p-3" data-testid={`catalog-offering-${o.catalog_item_id}`}>
              <div className="flex justify-between items-start gap-2 mb-1">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-heading font-bold text-text-primary truncate">{o.catalog_item_name}</h4>
                  {isCredit ? (
                    <div className="text-[11px] text-text-secondary mt-1 space-y-0.5">
                      {fmtRange(ct.ticket_size_min, ct.ticket_size_max) && <div><span className="text-text-muted">Ticket:</span> <span className="font-mono">&#x20b9;{fmtRange(ct.ticket_size_min, ct.ticket_size_max)}</span></div>}
                      {fmtRange(ct.interest_rate_min, ct.interest_rate_max) && <div><span className="text-text-muted">APR:</span> <span className="font-mono">{fmtRange(ct.interest_rate_min, ct.interest_rate_max)}%</span></div>}
                      {ct.tenure_months_max && <div><span className="text-text-muted">Tenure:</span> <span className="font-mono">{ct.tenure_months_min || 0}–{ct.tenure_months_max}m</span></div>}
                      {ct.collateral_type && <div><span className="text-text-muted">Collateral:</span> {String(ct.collateral_type).replace(/_/g, ' ')}</div>}
                    </div>
                  ) : (
                    <div className="mt-1">
                      <div className="text-lg font-mono font-bold text-text-primary">
                        {format((o.gross_price || 0) + (o.govt_fees || 0), o.currency || 'INR')}
                        <span className="text-[10px] text-text-muted font-normal ml-1">{CADENCES.find((c) => c.value === o.cadence)?.label || o.cadence}</span>
                      </div>
                      {o.govt_fees > 0 && (
                        <div className="text-[10px] text-text-muted" data-testid={`catalog-offering-split-${o.catalog_item_id}`}>
                          {format(o.gross_price || 0, o.currency || 'INR')} service + {format(o.govt_fees, o.currency || 'INR')} govt fees
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setPicker({ mode: 'edit', item: catalog.find((c) => c.id === o.catalog_item_id), existing: o })} data-testid={`catalog-offering-edit-${o.catalog_item_id}`} className="text-text-muted hover:text-text-primary text-xs">Edit</button>
                  <button onClick={() => remove(o)} data-testid={`catalog-offering-delete-${o.catalog_item_id}`} className="text-text-muted hover:text-danger"><Trash size={11} weight="bold" /></button>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-1">
                {o.margin_pct > 0 && <span className="text-[10px] text-text-muted">Founderboard margin: <span className="font-semibold" style={{ color: accent }}>{o.margin_pct}%</span></span>}
                {isCredit && ct.approval_time && <span className="text-[10px] text-text-muted">Approval: {ct.approval_time}</span>}
                {!isCredit && o.turnaround_days && <span className="text-[10px] text-text-muted">{o.turnaround_days}-day turnaround</span>}
              </div>
            </div>
            );
          })}
          {suggestionCards.map((s) => {
            const approved = s.status === 'approved';
            const rejected = s.status === 'rejected';
            const catalogItem = catalog.find((c) => c.id === s.id);
            return (
              <div key={s.id} className="bg-background border border-dashed border-border rounded-xl p-3" data-testid={`catalog-suggestion-${s.id}`}>
                <div className="flex justify-between items-start gap-2 mb-1">
                  <h4 className="text-sm font-heading font-bold text-text-primary truncate flex-1 min-w-0">{s.name}</h4>
                  <span data-testid={`catalog-suggestion-status-${s.id}`}
                    className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${
                      approved ? 'bg-success/10 text-success' : rejected ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning'}`}>
                    {approved ? 'Approved' : rejected ? 'Not approved' : 'Pending approval'}
                  </span>
                </div>
                {s.description && <p className="text-[11px] text-text-muted line-clamp-2">{s.description}</p>}
                {approved && catalogItem ? (
                  <button onClick={() => setPicker({ mode: 'new', preselect: catalogItem })} data-testid={`catalog-suggestion-price-${s.id}`}
                    className="mt-2 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg text-white inline-flex items-center gap-1" style={{ backgroundColor: accent }}>
                    <Plus size={10} weight="bold" /> Add your pricing
                  </button>
                ) : (
                  <p className="text-[10px] text-text-muted mt-2">
                    {rejected ? 'The Founderboard team didn’t add this to the catalog.' : 'Custom request — the Founderboard team is reviewing it. You can price it once it’s approved.'}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {picker && (
        <OfferingModal mode={picker.mode} accent={accent}
          catalogItems={picker.mode === 'edit' ? [picker.item].filter(Boolean) : availableItems}
          preselect={picker.preselect}
          initial={picker.existing}
          onClose={() => setPicker(null)}
          onSaved={() => { setPicker(null); reload(); }}
          saveEndpoint={saveEndpoint}
          canRequestDocs={parentKey === 'vendor'} />
      )}
      {suggestOpen && (
        <SuggestModal category={category} accent={accent}
          onClose={() => setSuggestOpen(false)}
          onSaved={() => { setSuggestOpen(false); toast.success('Submitted for review!'); reload(); }} />
      )}
    </div>
  );
};

const OfferingModal = ({ mode, accent, catalogItems, preselect, initial, onClose, onSaved, saveEndpoint, canRequestDocs }) => {
  const { format } = useCurrency();
  const isEdit = mode === 'edit';
  const [search, setSearch] = useState('');
  // `preselect` — opened from an approved suggestion card, so skip straight to pricing.
  const [picked, setPicked] = useState(isEdit ? catalogItems[0] : (preselect || null));
  const [form, setForm] = useState({
    gross_price: initial?.gross_price || 0,
    // (Iter 65) Statutory fees quoted separately from the service charge so
    // founders see "your fee + govt fee = total" rather than one lump number.
    govt_fees: initial?.govt_fees || 0,
    currency: initial?.currency || 'INR',
    cadence: initial?.cadence || 'one_time',
    margin_pct: initial?.margin_pct || 0,
    turnaround_days: initial?.turnaround_days || '',
  });
  // (Iter 47) required-docs picker state — pre-populated when editing.
  const [requiredDocs, setRequiredDocs] = useState(initial?.required_documents || []);
  const [vaultTaxonomy, setVaultTaxonomy] = useState([]);
  const [docPickerOpen, setDocPickerOpen] = useState(false);
  // Documents this vendor asked to add to the vault list, in any review state.
  const [docRequests, setDocRequests] = useState([]);
  // Payment terms and delivery timeline are no longer vendor-set: the backend
  // defaults an unset upfront % to 100%, and delivery falls back to turnaround_days.
  // (Iter 50) Credit-only — full product terms.
  const [creditTerms, setCreditTerms] = useState(() => ({
    ticket_size_min: initial?.credit_terms?.ticket_size_min ?? '',
    ticket_size_max: initial?.credit_terms?.ticket_size_max ?? '',
    interest_rate_min: initial?.credit_terms?.interest_rate_min ?? '',
    interest_rate_max: initial?.credit_terms?.interest_rate_max ?? '',
    processing_fee_value: initial?.credit_terms?.processing_fee_value ?? '',
    processing_fee_type: initial?.credit_terms?.processing_fee_type || 'percent',
    tenure_months_min: initial?.credit_terms?.tenure_months_min ?? '',
    tenure_months_max: initial?.credit_terms?.tenure_months_max ?? '',
    repayment_frequency: initial?.credit_terms?.repayment_frequency || 'monthly',
    collateral_type: initial?.credit_terms?.collateral_type || 'unsecured',
    elig_revenue: initial?.credit_terms?.eligibility?.min_monthly_revenue_inr ?? '',
    elig_months: initial?.credit_terms?.eligibility?.min_months_operation ?? '',
    elig_score: initial?.credit_terms?.eligibility?.min_credit_score ?? '',
    elig_exclusions: (initial?.credit_terms?.eligibility?.sector_exclusions || []).join(', '),
    disbursement_days_min: initial?.credit_terms?.disbursement_days_min ?? '',
    disbursement_days_max: initial?.credit_terms?.disbursement_days_max ?? '',
    approval_time: initial?.credit_terms?.approval_time || '48h',
  }));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/vault/structure');
        if (!cancelled) setVaultTaxonomy(r.data.datarooms || []);
      } catch {/* non-fatal, picker just won't open */}
      if (canRequestDocs) {
        try {
          const r = await api.get('/vault/document-requests/mine');
          if (!cancelled) setDocRequests(r.data.requests || []);
        } catch {/* non-fatal, request list just stays empty */}
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalogItems.slice(0, 30);
    return catalogItems.filter((c) => c.name?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q)).slice(0, 30);
  }, [catalogItems, search]);

  const submit = async () => {
    if (!picked) { toast.error('Pick a catalog item'); return; }
    const isCredit = picked?.category === 'credit';
    const isService = picked?.category === 'service';
    if (!isCredit && Number(form.gross_price) < 0) { toast.error('Price must be positive'); return; }
    if (!isCredit && Number(form.govt_fees || 0) < 0) { toast.error('Govt fees must be positive'); return; }
    // Service offerings carry platform-set bounds: charge ₹499–₹30,000, margin
    // 5–80%, and an optional turnaround capped at 60 days (blank allowed).
    if (isService) {
      const gp = Number(form.gross_price);
      if (!(gp >= 499 && gp <= 30000)) { toast.error('Service charge must be between ₹499 and ₹30,000'); return; }
      const mp = Number(form.margin_pct);
      if (!(mp >= 5 && mp <= 80)) { toast.error('Margin % must be between 5% and 80%'); return; }
      if (String(form.turnaround_days).trim() !== '') {
        const td = Number(form.turnaround_days);
        if (!(td >= 1 && td <= 60)) { toast.error('Turnaround days must be between 1 and 60 (or left blank)'); return; }
      }
    }
    if (isCredit) {
      // Sanity: if user filled a range, max must be >= min.
      const pairs = [['ticket_size_min', 'ticket_size_max'], ['interest_rate_min', 'interest_rate_max'], ['tenure_months_min', 'tenure_months_max'], ['disbursement_days_min', 'disbursement_days_max']];
      for (const [a, b] of pairs) {
        if (creditTerms[a] !== '' && creditTerms[b] !== '' && Number(creditTerms[b]) < Number(creditTerms[a])) {
          toast.error(`${b.replace(/_/g, ' ')} must be ≥ ${a.replace(/_/g, ' ')}`); return;
        }
      }
    }
    setBusy(true);
    try {
      const payload = {
        catalog_item_id: picked.id,
        gross_price: isCredit ? 0 : Number(form.gross_price),
        govt_fees: isCredit ? 0 : Number(form.govt_fees || 0),
        currency: form.currency,
        cadence: isCredit ? 'one_time' : form.cadence,
        margin_pct: Number(form.margin_pct || 0),
        turnaround_days: !isCredit && form.turnaround_days ? Number(form.turnaround_days) : null,
        required_documents: requiredDocs,
      };
      if (isCredit) {
        payload.credit_terms = {
          ticket_size_min: creditTerms.ticket_size_min,
          ticket_size_max: creditTerms.ticket_size_max,
          interest_rate_min: creditTerms.interest_rate_min,
          interest_rate_max: creditTerms.interest_rate_max,
          processing_fee_value: creditTerms.processing_fee_value,
          processing_fee_type: creditTerms.processing_fee_type,
          tenure_months_min: creditTerms.tenure_months_min,
          tenure_months_max: creditTerms.tenure_months_max,
          repayment_frequency: creditTerms.repayment_frequency,
          collateral_type: creditTerms.collateral_type,
          eligibility: {
            min_monthly_revenue_inr: creditTerms.elig_revenue,
            min_months_operation: creditTerms.elig_months,
            min_credit_score: creditTerms.elig_score,
            sector_exclusions: (creditTerms.elig_exclusions || '').split(',').map((s) => s.trim()).filter(Boolean),
          },
          disbursement_days_min: creditTerms.disbursement_days_min,
          disbursement_days_max: creditTerms.disbursement_days_max,
          approval_time: creditTerms.approval_time,
        };
      }
      await api.post(`${saveEndpoint}`, payload);
      toast.success(isEdit ? 'Updated' : 'Added');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-12 p-4 overflow-y-auto" onClick={onClose} data-testid="catalog-offering-modal">
      <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">{isEdit ? 'Edit offering' : 'Add a catalog offering'}</h3>
          <button onClick={onClose}><X size={16} weight="bold" /></button>
        </div>

        {!isEdit && (
          <div className="mb-4">
            <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">1. Pick what you offer</label>
            <div className="relative mb-2">
              <MagnifyingGlass size={11} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search catalog…" data-testid="catalog-offering-search" className="w-full pl-7 pr-3 py-2 bg-background border border-border rounded-lg text-sm" />
            </div>
            <div className="max-h-56 overflow-y-auto border border-border rounded-lg bg-background">
              {filtered.length === 0 ? (
                <p className="text-xs text-text-muted text-center py-4">No matches</p>
              ) : filtered.map((c) => (
                <button key={c.id} onClick={() => setPicked(c)}
                  data-testid={`catalog-pick-${c.id}`}
                  className={`w-full text-left px-3 py-2 hover:bg-surface border-b border-border last:border-b-0 ${picked?.id === c.id ? 'bg-surface' : ''}`}>
                  <div className="text-sm font-medium text-text-primary">{c.name}</div>
                  <div className="text-[11px] text-text-muted line-clamp-1">{c.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {picked && (
          <div className="space-y-3">
            <div className="bg-background border border-border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-0.5">Selected</div>
              <div className="text-sm font-semibold text-text-primary">{picked.name}</div>
              <div className="text-[11px] text-text-muted">{picked.description}</div>
            </div>

            {/* Non-credit (service / agency) → standard price + cadence + turnaround.
                Credit → renders the 11-field credit terms block below instead. */}
            {picked?.category !== 'credit' && (
              <>
                <div className="grid grid-cols-[1fr_90px_140px] gap-3">
                  <Inp label={picked?.category === 'service' ? 'Service charge *' : 'Price *'} type="number" v={form.gross_price} on={(v) => setForm((p) => ({ ...p, gross_price: v }))} testid="catalog-offering-price"
                    min={picked?.category === 'service' ? 499 : undefined} max={picked?.category === 'service' ? 30000 : undefined}
                    hint={picked?.category === 'service' ? '₹499 – ₹30,000' : undefined} />
                  <Sel label="Currency" v={form.currency} on={(v) => setForm((p) => ({ ...p, currency: v }))} options={['INR', 'USD']} testid="catalog-offering-currency" />
                  <Sel label="Cadence" v={form.cadence} on={(v) => setForm((p) => ({ ...p, cadence: v }))} options={CADENCES.map((c) => c.value)} optionLabels={CADENCES.map((c) => c.label)} testid="catalog-offering-cadence" />
                </div>
                {/* (Iter 65) Services quote statutory fees separately — founders see
                    the split, and it's excluded from your margin. */}
                {picked?.category === 'service' && (
                  <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
                    <Inp label="Govt / statutory fees" type="number" v={form.govt_fees} on={(v) => setForm((p) => ({ ...p, govt_fees: v }))} testid="catalog-offering-govt-fees" />
                    <div className="pb-2 text-right">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Founder pays</div>
                      <div className="text-sm font-mono font-bold" style={{ color: accent }} data-testid="catalog-offering-total">
                        {format(Number(form.gross_price || 0) + Number(form.govt_fees || 0), form.currency)}
                      </div>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Inp label="Margin % (internal)" type="number" v={form.margin_pct} on={(v) => setForm((p) => ({ ...p, margin_pct: v }))} testid="catalog-offering-margin"
                    min={picked?.category === 'service' ? 5 : undefined} max={picked?.category === 'service' ? 80 : undefined}
                    hint={picked?.category === 'service' ? '5% – 80%' : undefined} />
                  <Inp label="Turnaround days (optional)" type="number" v={form.turnaround_days} on={(v) => setForm((p) => ({ ...p, turnaround_days: v }))} testid="catalog-offering-turnaround"
                    max={picked?.category === 'service' ? 60 : undefined}
                    hint={picked?.category === 'service' ? 'Optional · max 60 days' : undefined} />
                </div>
              </>
            )}

            {/* (Iter 50) Credit-only: 11-field product terms. */}
            {picked?.category === 'credit' && (
              <CreditTermsBlock value={creditTerms} onChange={setCreditTerms} accent={accent} />
            )}

            <RequiredDocsField
              vaultTaxonomy={vaultTaxonomy}
              value={requiredDocs}
              onChange={setRequiredDocs}
              accent={accent}
              open={docPickerOpen}
              onToggle={() => setDocPickerOpen((x) => !x)}
              canRequestDocs={canRequestDocs}
              docRequests={docRequests}
              onRequested={(req) => setDocRequests((p) => [req, ...p])}
            />
            <button onClick={submit} disabled={busy} data-testid="catalog-offering-save" className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: accent }}>
              <FloppyDisk size={11} weight="bold" /> {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Add to my catalog'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// (Iter 50) Credit-only product terms block — 11 fields per credit product spec.
const CreditTermsBlock = ({ value, onChange, accent }) => {
  const set = (k) => (v) => onChange((p) => ({ ...p, [k]: v }));
  return (
    <div className="bg-background border border-border rounded-lg p-3 space-y-3" data-testid="catalog-offering-credit-extras">
      <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: accent }}>Credit product terms</div>
      <p className="text-[10px] text-text-muted -mt-2">All ranges are indicative — actual terms are subject to your underwriting at intro time.</p>

      <div>
        <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Ticket size range (INR)</label>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" placeholder="Min e.g. 100000" value={value.ticket_size_min} onChange={(e) => set('ticket_size_min')(e.target.value)} data-testid="credit-ticket-min" className="bg-surface border border-border rounded-lg px-3 py-2 text-sm" />
          <input type="number" placeholder="Max e.g. 5000000" value={value.ticket_size_max} onChange={(e) => set('ticket_size_max')(e.target.value)} data-testid="credit-ticket-max" className="bg-surface border border-border rounded-lg px-3 py-2 text-sm" />
        </div>
      </div>

      <div>
        <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Interest rate / APR range (%)</label>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" step="0.1" placeholder="Min e.g. 12" value={value.interest_rate_min} onChange={(e) => set('interest_rate_min')(e.target.value)} data-testid="credit-apr-min" className="bg-surface border border-border rounded-lg px-3 py-2 text-sm" />
          <input type="number" step="0.1" placeholder="Max e.g. 18" value={value.interest_rate_max} onChange={(e) => set('interest_rate_max')(e.target.value)} data-testid="credit-apr-max" className="bg-surface border border-border rounded-lg px-3 py-2 text-sm" />
        </div>
      </div>

      <div className="grid grid-cols-[1fr_120px] gap-2">
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Processing fee</label>
          <input type="number" step="0.01" placeholder="e.g. 1.5 or 2999" value={value.processing_fee_value} onChange={(e) => set('processing_fee_value')(e.target.value)} data-testid="credit-fee-value" className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Fee type</label>
          <select value={value.processing_fee_type} onChange={(e) => set('processing_fee_type')(e.target.value)} data-testid="credit-fee-type" className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm">
            <option value="percent">% of ticket</option>
            <option value="flat">Flat (INR)</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Tenure / repayment period (months)</label>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" placeholder="Min e.g. 3" value={value.tenure_months_min} onChange={(e) => set('tenure_months_min')(e.target.value)} data-testid="credit-tenure-min" className="bg-surface border border-border rounded-lg px-3 py-2 text-sm" />
          <input type="number" placeholder="Max e.g. 24" value={value.tenure_months_max} onChange={(e) => set('tenure_months_max')(e.target.value)} data-testid="credit-tenure-max" className="bg-surface border border-border rounded-lg px-3 py-2 text-sm" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Repayment frequency</label>
          <select value={value.repayment_frequency} onChange={(e) => set('repayment_frequency')(e.target.value)} data-testid="credit-repayment" className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm">
            <option value="monthly">Monthly EMI</option>
            <option value="weekly">Weekly</option>
            <option value="bullet">Bullet (end of tenure)</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Collateral required</label>
          <select value={value.collateral_type} onChange={(e) => set('collateral_type')(e.target.value)} data-testid="credit-collateral" className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm">
            <option value="unsecured">Unsecured</option>
            <option value="personal_guarantee">Personal guarantee</option>
            <option value="lien_on_receivables">Lien on receivables</option>
          </select>
        </div>
      </div>

      <div className="border-t border-border pt-3">
        <div className="text-[11px] uppercase tracking-wider font-bold text-text-muted mb-2">Eligibility criteria</div>
        <div className="grid grid-cols-2 gap-2">
          <Inp label="Min monthly revenue (INR)" type="number" v={value.elig_revenue} on={set('elig_revenue')} testid="credit-elig-revenue" />
          <Inp label="Min months in operation" type="number" v={value.elig_months} on={set('elig_months')} testid="credit-elig-months" />
          <Inp label="Min credit score" type="number" v={value.elig_score} on={set('elig_score')} testid="credit-elig-score" />
          <Inp label="Sector exclusions (comma-sep)" v={value.elig_exclusions} on={set('elig_exclusions')} testid="credit-elig-exclusions" />
        </div>
      </div>

      <div className="border-t border-border pt-3 grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Disbursement timeline (days)</label>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" placeholder="Min" value={value.disbursement_days_min} onChange={(e) => set('disbursement_days_min')(e.target.value)} data-testid="credit-disburse-min" className="bg-surface border border-border rounded-lg px-3 py-2 text-sm" />
            <input type="number" placeholder="Max" value={value.disbursement_days_max} onChange={(e) => set('disbursement_days_max')(e.target.value)} data-testid="credit-disburse-max" className="bg-surface border border-border rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Approval / decisioning time</label>
          <select value={value.approval_time} onChange={(e) => set('approval_time')(e.target.value)} data-testid="credit-approval-time" className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm">
            <option value="instant">Instant</option>
            <option value="24h">Within 24 hours</option>
            <option value="48h">Within 48 hours</option>
            <option value="72h">Within 72 hours</option>
            <option value="7d">Up to 7 days</option>
          </select>
        </div>
      </div>
    </div>
  );
};

const SuggestModal = ({ category, accent, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: '', description: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (form.name.trim().length < 3) { toast.error('Name must be at least 3 chars'); return; }
    setBusy(true);
    try {
      await api.post('/catalog/suggest', { category, name: form.name.trim(), description: form.description.trim() });
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-16 p-4" onClick={onClose} data-testid="catalog-suggest-modal">
      <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">Suggest a new {category} catalog item</h3>
          <button onClick={onClose}><X size={16} weight="bold" /></button>
        </div>
        <p className="text-xs text-text-muted mb-3">Sid &amp; Lalith will review and add it within 48 hours. Once approved, it shows up for every vendor.</p>
        <div className="space-y-3">
          <Inp label="Name *" v={form.name} on={(v) => setForm((p) => ({ ...p, name: v }))} testid="catalog-suggest-name" />
          <Txt label="Short description" v={form.description} on={(v) => setForm((p) => ({ ...p, description: v }))} testid="catalog-suggest-desc" />
        </div>
        <button onClick={submit} disabled={busy} data-testid="catalog-suggest-submit" className="mt-4 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: accent }}>
          <Lightbulb size={11} weight="bold" /> {busy ? 'Submitting…' : 'Submit for review'}
        </button>
      </div>
    </div>
  );
};

const Inp = ({ label, v, on, type = 'text', testid, min, max, hint }) => {
  // Numeric fields (price, govt fees, margin %, turnaround/delivery days) must
  // accept digits and at most one decimal point only. type="number" alone is not
  // enough — mobile Safari (iPad) lets arbitrary text through — so we gate the
  // change and swallow anything non-numeric. Empty is allowed for clearing.
  // min/max are native hints only; the hard range check lives in submit().
  const handle = (e) => {
    const val = e.target.value;
    if (type === 'number' && val !== '' && !/^\d*\.?\d*$/.test(val)) return;
    on(val);
  };
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
      <input type={type} inputMode={type === 'number' ? 'decimal' : undefined} min={min} max={max} value={v} onChange={handle} data-testid={testid} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
      {hint && <div className="text-[10px] text-text-muted mt-1">{hint}</div>}
    </div>
  );
};
const Txt = ({ label, v, on, testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
    <textarea value={v} onChange={(e) => on(e.target.value)} rows={3} data-testid={testid} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
  </div>
);
const Sel = ({ label, v, on, options, optionLabels, testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
    <div className="relative">
      <select value={v} onChange={(e) => on(e.target.value)} data-testid={testid} className="appearance-none w-full bg-background border border-border rounded-lg pl-3 pr-7 py-2 text-sm">
        {options.map((o, i) => <option key={o} value={o}>{optionLabels?.[i] || o}</option>)}
      </select>
      <CaretDown size={9} weight="bold" className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
    </div>
  </div>
);

// (Iter 47) Lets the vendor pick which of the vault slots they need from the
// founder for this offering.  Collapsed by default — vendor expands to choose.
const RequiredDocsField = ({ vaultTaxonomy, value, onChange, accent, open, onToggle, canRequestDocs, docRequests = [], onRequested }) => {
  const [search, setSearch] = useState('');
  // Request a document that isn't in the vault list. It goes to the admin
  // console for approval, and only then shows up in this picker and the vault.
  const [requestOpen, setRequestOpen] = useState(false);
  const [reqForm, setReqForm] = useState({ name: '', dataroom_id: '', description: '' });
  const [reqBusy, setReqBusy] = useState(false);
  const requestableRooms = useMemo(() => vaultTaxonomy.filter((dr) => !dr.read_only), [vaultTaxonomy]);
  const openRequests = useMemo(() => docRequests.filter((r) => r.status !== 'approved'), [docRequests]);
  const submitRequest = async () => {
    if (reqForm.name.trim().length < 3) { toast.error('Document name must be at least 3 characters'); return; }
    if (!reqForm.dataroom_id) { toast.error('Pick the category this document belongs in'); return; }
    setReqBusy(true);
    try {
      const r = await api.post('/vault/document-requests', {
        name: reqForm.name.trim(), dataroom_id: reqForm.dataroom_id, description: reqForm.description.trim(),
      });
      onRequested?.(r.data.request);
      toast.success('Sent for admin review — it will appear in this list once approved.');
      setReqForm({ name: '', dataroom_id: '', description: '' });
      setRequestOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setReqBusy(false); }
  };
  const selectedSet = useMemo(() => new Set(value.map((v) => `${v.dataroom_id}::${v.slot_id}`)), [value]);
  const toggle = (drId, slotId) => {
    const key = `${drId}::${slotId}`;
    if (selectedSet.has(key)) onChange(value.filter((v) => `${v.dataroom_id}::${v.slot_id}` !== key));
    else onChange([...value, { dataroom_id: drId, slot_id: slotId }]);
  };
  // Docs are mandatory unless the vendor ticks "Optional" — the flag is only
  // sent when set, matching how the backend stores it.
  const setOptional = (drId, slotId, optional) => onChange(value.map((v) => {
    if (v.dataroom_id !== drId || v.slot_id !== slotId) return v;
    const base = { dataroom_id: v.dataroom_id, slot_id: v.slot_id };
    return optional ? { ...base, optional: true } : base;
  }));
  const filteredDatarooms = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vaultTaxonomy;
    return vaultTaxonomy
      .map((dr) => ({ ...dr, slots: dr.slots.filter((s) => s.name.toLowerCase().includes(q) || dr.name.toLowerCase().includes(q)) }))
      .filter((dr) => dr.slots.length);
  }, [vaultTaxonomy, search]);

  return (
    <div className="bg-background border border-border rounded-lg" data-testid="required-docs-field">
      <button type="button" onClick={onToggle} data-testid="required-docs-toggle"
        className="w-full flex items-center justify-between px-3 py-2.5 text-left">
        <div className="flex items-center gap-2 min-w-0">
          <FileText size={12} weight="bold" style={{ color: accent }} />
          <span className="text-[11px] uppercase tracking-wider font-bold text-text-muted">Required documents</span>
          {value.length > 0 && (
            <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${accent}1a`, color: accent }}>{value.length}</span>
          )}
        </div>
        <CaretDown size={10} weight="bold" className="text-text-muted" style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {open && (
        <div className="border-t border-border p-3">
          <p className="text-[11px] text-text-muted mb-2">Tick the docs you need from founders to deliver this offering. Founders will see this list (with ✓ for what they&rsquo;ve already uploaded) when browsing your offering. Every doc is mandatory unless you mark it optional.</p>
          {value.length > 0 && (
            <div className="mb-2 rounded border border-border bg-surface divide-y divide-border/60" data-testid="required-docs-selected">
              {value.map((v) => {
                const dr = vaultTaxonomy.find((d) => d.id === v.dataroom_id);
                const slot = dr?.slots.find((s) => s.id === v.slot_id);
                return (
                  <div key={`${v.dataroom_id}::${v.slot_id}`} className="flex items-center gap-2 px-2.5 py-1.5">
                    <span className="flex-1 min-w-0 text-xs text-text-primary truncate">{slot?.name || v.slot_id}</span>
                    <label className="inline-flex items-center gap-1.5 text-[10px] font-medium text-text-muted cursor-pointer flex-shrink-0">
                      <input type="checkbox" checked={!!v.optional}
                        onChange={(e) => setOptional(v.dataroom_id, v.slot_id, e.target.checked)}
                        data-testid={`required-doc-optional-${v.slot_id}`}
                        className="w-3.5 h-3.5" style={{ accentColor: accent }} />
                      Optional
                    </label>
                    <button type="button" onClick={() => toggle(v.dataroom_id, v.slot_id)} className="text-text-muted hover:text-danger flex-shrink-0">
                      <X size={9} weight="bold" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="relative mb-2">
            <MagnifyingGlass size={11} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search docs…"
              data-testid="required-docs-search"
              className="w-full pl-7 pr-3 py-1.5 bg-surface border border-border rounded text-xs" />
          </div>
          <div className="max-h-56 overflow-y-auto rounded border border-border bg-surface" data-testid="required-docs-list">
            {filteredDatarooms.length === 0 ? (
              <p className="text-xs text-text-muted text-center py-4">No matches</p>
            ) : filteredDatarooms.map((dr) => (
              <div key={dr.id}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted px-2.5 py-1 bg-background sticky top-0 z-[1]">{dr.name}</div>
                {dr.slots.map((s) => {
                  const key = `${dr.id}::${s.id}`;
                  const sel = selectedSet.has(key);
                  return (
                    <button type="button" key={s.id} onClick={() => toggle(dr.id, s.id)}
                      data-testid={`required-doc-toggle-${s.id}`}
                      className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 hover:bg-background border-b border-border/40 last:border-b-0 ${sel ? 'bg-background' : ''}`}>
                      <span className={`w-3.5 h-3.5 rounded inline-flex items-center justify-center flex-shrink-0 border ${sel ? '' : 'border-border'}`}
                            style={sel ? { backgroundColor: accent, borderColor: accent } : {}}>
                        {sel && <Check size={8} weight="bold" color="white" />}
                      </span>
                      <span className="text-xs text-text-primary">{s.name}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {canRequestDocs && (
            <div className="mt-2" data-testid="doc-request-section">
              {!requestOpen ? (
                <button type="button" onClick={() => setRequestOpen(true)} data-testid="doc-request-open"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: accent }}>
                  <Plus size={9} weight="bold" /> Can&rsquo;t find a document? Request it
                </button>
              ) : (
                <div className="rounded border border-border bg-surface p-2.5 space-y-2" data-testid="doc-request-form">
                  <p className="text-[10px] text-text-muted">An admin reviews every request. Once approved, the document is added to this list and to founders&rsquo; vaults under the category you pick.</p>
                  <input value={reqForm.name} onChange={(e) => setReqForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Document name, e.g. Form 15CA/15CB" maxLength={140} data-testid="doc-request-name"
                    className="w-full px-2.5 py-1.5 bg-background border border-border rounded text-xs" />
                  <select value={reqForm.dataroom_id} onChange={(e) => setReqForm((p) => ({ ...p, dataroom_id: e.target.value }))}
                    data-testid="doc-request-category"
                    className="w-full px-2.5 py-1.5 bg-background border border-border rounded text-xs">
                    <option value="">Category it belongs in…</option>
                    {requestableRooms.map((dr) => <option key={dr.id} value={dr.id}>{dr.name}</option>)}
                  </select>
                  <textarea value={reqForm.description} onChange={(e) => setReqForm((p) => ({ ...p, description: e.target.value }))}
                    rows={2} placeholder="Why you need it (optional)" maxLength={600} data-testid="doc-request-desc"
                    className="w-full px-2.5 py-1.5 bg-background border border-border rounded text-xs" />
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setRequestOpen(false)} className="text-[11px] px-2.5 py-1 border border-border rounded">Cancel</button>
                    <button type="button" onClick={submitRequest} disabled={reqBusy} data-testid="doc-request-submit"
                      className="text-[11px] px-2.5 py-1 rounded font-semibold text-white disabled:opacity-50" style={{ backgroundColor: accent }}>
                      {reqBusy ? 'Sending…' : 'Send for approval'}
                    </button>
                  </div>
                </div>
              )}
              {openRequests.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="doc-request-list">
                  {openRequests.map((r) => (
                    <div key={r.id} className="flex items-start gap-2 text-[11px]" data-testid={`doc-request-${r.id}`}>
                      <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold ${r.status === 'pending' ? 'bg-amber-500/15 text-amber-500' : 'bg-red-500/15 text-red-500'}`}>
                        {r.status === 'pending' ? 'Pending review' : 'Rejected'}
                      </span>
                      <span className="min-w-0 text-text-primary">
                        {r.name}
                        <span className="text-text-muted"> · {vaultTaxonomy.find((d) => d.id === r.dataroom_id)?.name || r.dataroom_id}</span>
                        {r.status === 'rejected' && r.reject_reason && <span className="block text-text-muted">{r.reject_reason}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CatalogPicker;
